import { useMemo, useRef, useState } from "react";
import {
  computePreviewPanelPosition,
  sliderPreviewOffsetFromPointer,
  sliderPreviewTimeFromPointer,
} from "@/pages/playerTrickplay";

const HARNESS_DURATION_SECONDS = 60 * 60;
const PREVIEW_PANEL_WIDTH_PX = 160;

function formatTime(seconds: number): string {
  const wholeSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(wholeSeconds / 3600);
  const minutes = Math.floor((wholeSeconds % 3600) / 60);
  const remainingSeconds = wholeSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

type DebugSnapshot = {
  pointerClientX: number | null;
  wrapperLeft: number;
  wrapperWidth: number;
  inputLeft: number;
  inputWidth: number;
  wrapperRatio: number | null;
  inputRatio: number | null;
  hoverTime: number | null;
  sliderValue: number;
};

export function SeekHarnessPage() {
  const seekWrapRef = useRef<HTMLDivElement>(null);
  const seekPointerActiveRef = useRef(false);
  const seekInteractionValueRef = useRef<number | null>(null);
  const [scrubbing, setScrubbing] = useState(false);
  const [scrubTime, setScrubTime] = useState<number | null>(null);
  const [seekHoverTime, setSeekHoverTime] = useState<number | null>(null);
  const [seekHoverOffsetPx, setSeekHoverOffsetPx] = useState<number | null>(null);
  const [seekRailWidthPx, setSeekRailWidthPx] = useState(0);
  const [committedTime, setCommittedTime] = useState(HARNESS_DURATION_SECONDS / 2);
  const [debugSnapshot, setDebugSnapshot] = useState<DebugSnapshot | null>(null);

  const displayedTime = seekHoverTime ?? (scrubbing && scrubTime !== null ? scrubTime : committedTime);
  const progressPercent = Math.max(0, Math.min(100, (displayedTime / HARNESS_DURATION_SECONDS) * 100));
  const previewLayout = seekHoverOffsetPx !== null
    ? computePreviewPanelPosition(seekHoverOffsetPx, seekRailWidthPx, PREVIEW_PANEL_WIDTH_PX)
    : null;

  function setTimeState(nextTime: number, target: HTMLElement, options?: { pointerClientX?: number | null; publishEvent?: string }) {
    const clamped = Math.max(0, Math.min(HARNESS_DURATION_SECONDS, nextTime));
    const { wrapperRect } = geometryRects(target);
    setSeekRailWidthPx(wrapperRect.width);
    setSeekHoverOffsetPx((clamped / HARNESS_DURATION_SECONDS) * wrapperRect.width);
    setScrubbing(true);
    setScrubTime(clamped);
    setSeekHoverTime(clamped);
    seekInteractionValueRef.current = clamped;
    publishSnapshot(options?.pointerClientX ?? null, target, clamped, clamped);
  }

  function commitTime(nextTime: number) {
    const clamped = Math.max(0, Math.min(HARNESS_DURATION_SECONDS, nextTime));
    seekInteractionValueRef.current = clamped;
    setCommittedTime(clamped);
    setScrubbing(false);
    setScrubTime(null);
    setSeekHoverTime(clamped);
  }

  function geometryRects(target: HTMLElement): { wrapperRect: DOMRect; inputRect: DOMRect } {
    return {
      wrapperRect: seekWrapRef.current?.getBoundingClientRect() ?? target.getBoundingClientRect(),
      inputRect: target.getBoundingClientRect(),
    };
  }

  function publishSnapshot(pointerClientX: number | null, target: HTMLElement, hoverTime: number | null, sliderValue: number) {
    const { wrapperRect, inputRect } = geometryRects(target);
    const clampRatio = (value: number) => Math.max(0, Math.min(1, value));
    const snapshot: DebugSnapshot = {
      pointerClientX,
      wrapperLeft: wrapperRect.left,
      wrapperWidth: wrapperRect.width,
      inputLeft: inputRect.left,
      inputWidth: inputRect.width,
      wrapperRatio: pointerClientX !== null && wrapperRect.width > 0
        ? clampRatio((pointerClientX - wrapperRect.left) / wrapperRect.width)
        : null,
      inputRatio: pointerClientX !== null && inputRect.width > 0
        ? clampRatio((pointerClientX - inputRect.left) / inputRect.width)
        : null,
      hoverTime,
      sliderValue,
    };
    setDebugSnapshot(snapshot);
    (window as Window & { __knotfinSeekHarness?: DebugSnapshot }).__knotfinSeekHarness = snapshot;
  }

  function updatePointerGeometry(pointerClientX: number, target: HTMLElement) {
    const { wrapperRect } = geometryRects(target);
    setSeekRailWidthPx(wrapperRect.width);
    setSeekHoverOffsetPx(sliderPreviewOffsetFromPointer(pointerClientX, wrapperRect));
  }

  function pointerTime(pointerClientX: number, target: HTMLElement): number {
    const { wrapperRect } = geometryRects(target);
    return sliderPreviewTimeFromPointer(pointerClientX, wrapperRect, HARNESS_DURATION_SECONDS);
  }

  function clearHover() {
    if (!scrubbing) {
      setSeekHoverTime(null);
      setSeekHoverOffsetPx(null);
    }
  }

  const debugLines = useMemo(() => {
    if (!debugSnapshot) {
      return [];
    }
    return [
      `pointer ${debugSnapshot.pointerClientX !== null ? debugSnapshot.pointerClientX.toFixed(1) : "--"}`,
      `wrapper ${debugSnapshot.wrapperLeft.toFixed(1)} + ${debugSnapshot.wrapperWidth.toFixed(1)}`,
      `input ${debugSnapshot.inputLeft.toFixed(1)} + ${debugSnapshot.inputWidth.toFixed(1)}`,
      `wrapper ratio ${debugSnapshot.wrapperRatio !== null ? `${(debugSnapshot.wrapperRatio * 100).toFixed(2)}%` : "--"}`,
      `input ratio ${debugSnapshot.inputRatio !== null ? `${(debugSnapshot.inputRatio * 100).toFixed(2)}%` : "--"}`,
      `hover ${debugSnapshot.hoverTime !== null ? formatTime(debugSnapshot.hoverTime) : "--:--"}`,
      `slider ${formatTime(debugSnapshot.sliderValue)}`,
    ];
  }, [debugSnapshot]);

  return (
    <div className="player-page seek-harness-page">
      <div className="seek-harness-shell">
        <h1 className="seek-harness-title">Seek Harness</h1>
        <p className="seek-harness-copy">
          Deterministic browser harness for the web player seek control.
        </p>
        <div className="player-progress seek-harness-progress">
          <span className="player-time">{formatTime(displayedTime)}</span>
          <div className="player-seek-wrap" ref={seekWrapRef} data-testid="seek-harness-wrap">
            <div className="player-seek-rail" aria-hidden="true">
              <div className="player-seek-rail-fill" style={{ width: `${progressPercent}%` }} />
            </div>
            {seekHoverOffsetPx !== null && (
              <div
                className="seek-harness-pointer-guide"
                aria-hidden="true"
                style={{ left: `${seekHoverOffsetPx}px` }}
              />
            )}
            {previewLayout && seekHoverTime !== null && (
              <div
                className="player-trickplay-preview"
                data-testid="seek-harness-preview"
                style={{ left: `${previewLayout.leftPx}px`, width: `${PREVIEW_PANEL_WIDTH_PX}px` }}
              >
                <div
                  className="player-trickplay-preview-pointer"
                  data-testid="seek-harness-preview-pointer"
                  style={{ left: `${previewLayout.pointerWithinPanelPx}px` }}
                />
                <div className="player-trickplay-preview-image seek-harness-preview-image" />
                <div className="player-trickplay-preview-time">{formatTime(seekHoverTime)}</div>
              </div>
            )}
            <div
              role="slider"
              aria-label="Seek Harness"
              aria-valuemin={0}
              aria-valuemax={HARNESS_DURATION_SECONDS}
              aria-valuenow={Math.floor(displayedTime)}
              aria-valuetext={formatTime(displayedTime)}
              className="player-seek"
              data-testid="seek-harness-input"
              tabIndex={0}
              onPointerDown={(event) => {
                if (event.currentTarget.setPointerCapture) {
                  event.currentTarget.setPointerCapture(event.pointerId);
                }
                seekPointerActiveRef.current = true;
                const nextTime = pointerTime(event.clientX, event.currentTarget);
                updatePointerGeometry(event.clientX, event.currentTarget);
                setTimeState(nextTime, event.currentTarget, { pointerClientX: event.clientX, publishEvent: "pointer_down" });
              }}
              onPointerMove={(event) => {
                if (seekPointerActiveRef.current) {
                  const nextTime = pointerTime(event.clientX, event.currentTarget);
                  updatePointerGeometry(event.clientX, event.currentTarget);
                  setTimeState(nextTime, event.currentTarget, { pointerClientX: event.clientX, publishEvent: "pointer_move" });
                  return;
                }
                const nextTime = pointerTime(event.clientX, event.currentTarget);
                updatePointerGeometry(event.clientX, event.currentTarget);
                setSeekHoverTime(nextTime);
                publishSnapshot(event.clientX, event.currentTarget, nextTime, nextTime);
              }}
              onPointerUp={(event) => {
                const nextTime = seekPointerActiveRef.current
                  ? pointerTime(event.clientX, event.currentTarget)
                  : displayedTime;
                seekPointerActiveRef.current = false;
                commitTime(nextTime);
                publishSnapshot(event.clientX, event.currentTarget, nextTime, nextTime);
              }}
              onPointerCancel={(event) => {
                seekPointerActiveRef.current = false;
                clearHover();
                publishSnapshot(event.clientX, event.currentTarget, null, displayedTime);
              }}
              onMouseLeave={clearHover}
              onKeyDown={(event) => {
                let nextTime: number | null = null;
                switch (event.key) {
                  case "ArrowLeft":
                    nextTime = displayedTime - 1;
                    break;
                  case "ArrowRight":
                    nextTime = displayedTime + 1;
                    break;
                  case "PageDown":
                    nextTime = displayedTime - 10;
                    break;
                  case "PageUp":
                    nextTime = displayedTime + 10;
                    break;
                  case "Home":
                    nextTime = 0;
                    break;
                  case "End":
                    nextTime = HARNESS_DURATION_SECONDS;
                    break;
                }
                if (nextTime === null) {
                  return;
                }
                event.preventDefault();
                setTimeState(nextTime, event.currentTarget);
              }}
              onKeyUp={(event) => {
                if (!["ArrowLeft", "ArrowRight", "PageDown", "PageUp", "Home", "End"].includes(event.key)) {
                  return;
                }
                commitTime(seekInteractionValueRef.current ?? displayedTime);
              }}
            >
              <div
                className="player-seek-thumb"
                aria-hidden="true"
                style={{ left: `${progressPercent}%` }}
              />
            </div>
          </div>
          <span className="player-time">{formatTime(HARNESS_DURATION_SECONDS)}</span>
        </div>
        <div className="player-seek-debug seek-harness-debug" data-testid="seek-harness-debug">
          {debugLines.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
      </div>
    </div>
  );
}
